import { Link } from 'react-router-dom';
import Seo from '../seo/Seo.jsx';
import { metaFor } from '../seo/routes.js';
import { school } from '../constants/school.js';

/*
 * The admissions form collects a child's name and date of birth. That makes
 * this page a legal requirement in most jurisdictions, not a nicety — and the
 * form links to it directly above the submit button, which is where a parent
 * actually wants it.
 *
 * The retention period and the contact address below are the two things a
 * school has to confirm against its own policy before this goes live.
 */
export default function Privacy() {
  return (
    <>
      <Seo {...metaFor('/privacy')} path="/privacy" />

      <article className="mx-auto max-w-[720px] px-5 py-20 sm:px-8">
        <p className="font-hand text-2xl text-coral">Being straight with you</p>
        <h1 className="mt-1.5 font-display text-[clamp(32px,5vw,52px)] font-extrabold leading-[1.05] text-forest">
          What we do with what you tell us
        </h1>
        <p className="mt-5 text-lg leading-[1.6] text-ink-soft">
          Last updated 1 August 2026. This covers the enquiry form on this site. Once a
          child is enrolled, the school's full data policy applies and we give you a copy
          of it.
        </p>

        <Section title="What we collect">
          <p>Only what the enquiry form asks for:</p>
          <ul className="mt-3 list-disc space-y-1.5 pl-5">
            <li>your name, email address and, if you give it, your phone number</li>
            <li>your child's first name and, if you give it, their date of birth</li>
            <li>which stage you are asking about and when you would like to start</li>
            <li>anything you write in the message box</li>
          </ul>
          <p className="mt-3">
            We do not use tracking cookies, advertising pixels or third-party analytics on
            this site. The only cookie we set is the one that keeps a member of staff
            signed in to the admissions dashboard.
          </p>
        </Section>

        <Section title="Why we need it">
          <p>
            To answer your enquiry, arrange a visit, and keep track of where your
            application has got to. We do not use it for anything else, and we never sell
            it or share it with anyone outside the school.
          </p>
        </Section>

        <Section title="Who can see it">
          <p>
            Named admissions staff, signed in to an account we control. Every account has
            its own password and can be switched off the same day someone leaves. Nothing
            is public and nothing is emailed onward.
          </p>
        </Section>

        <Section title="How long we keep it">
          <p>
            If your child joins us, the enquiry becomes part of their admission record. If
            they do not, we delete the enquiry twelve months after we last heard from you
            — long enough that you can come back to us the following September without
            starting again.
          </p>
        </Section>

        <Section title="Asking us to change or delete it">
          <p>
            Email{' '}
            <a
              className="font-bold text-forest underline"
              href={`mailto:${school.email}`}
            >
              {school.email}
            </a>{' '}
            or call{' '}
            <a
              className="font-bold text-forest underline"
              href={`tel:${school.phone.replace(/\s/g, '')}`}
            >
              {school.phone}
            </a>
            . You can ask for a copy of what we hold, ask us to correct it, or ask us to
            delete it. We will do it within thirty days and we will not ask you why.
          </p>
        </Section>

        <p className="mt-12">
          <Link className="font-bold text-forest underline" to="/">
            ← Back to the homepage
          </Link>
        </p>
      </article>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-[26px] font-extrabold text-forest">{title}</h2>
      <div className="mt-2.5 space-y-3 text-[17px] leading-[1.6] text-ink-soft">
        {children}
      </div>
    </section>
  );
}
